import { useTranslation } from 'react-i18next';
import {
  Icon,
  IconNames,
  LoadingIndicator,
  SpinnerSizes,
} from '@triumph/design-system';
import { useAppContext } from '@/app/components/providers/AppContextProvider';
import { resolveCarrierBadge } from '@/app/shared/utils/noaUtils';
import {
  CarrierNoaDetail,
  CarrierNoaStatus,
  NoaDocumentType,
} from '../noaCenter.types';
import { StatusBadge } from './StatusBadge';
import { NoaDocumentCard } from './NoaDocumentCard';
import {
  DetailCarrierName,
  DetailHeader,
  DetailMeta,
  DetailPanel as DetailPanelCard,
  DetailSectionLabel,
  EmptyState,
  EmptyStateIconTile,
  EmptyStateText,
  EmptyStateTitle,
  NoDocumentState,
  NoDocumentIconTile,
  NoDocumentTitle,
  NoDocumentText,
  StateWrapper,
} from '../NOACenter.StyledComponents';

interface CarrierDetailPanelProps {
  detail: CarrierNoaDetail | null | undefined;
  isLoading: boolean;
  isError?: boolean;
  hasSelection: boolean;
}

export const CarrierDetailPanel: React.FC<CarrierDetailPanelProps> = ({
  detail,
  isLoading,
  isError = false,
  hasSelection,
}) => {
  const { t } = useTranslation('noaCenter');
  const { theme } = useAppContext();

  if (!hasSelection) {
    return (
      <DetailPanelCard data-testid="noa-detail-panel">
        <EmptyState data-testid="noa-no-selection">
          <EmptyStateIconTile>
            <Icon
              name={IconNames.Filetype_Document}
              id="noa-no-selection-icon"
              size="md"
              color={theme.colors.greyscale.gray40}
            />
          </EmptyStateIconTile>
          <EmptyStateTitle>{t('emptyStates.noSelectionTitle')}</EmptyStateTitle>
          <EmptyStateText>{t('emptyStates.noSelectionText')}</EmptyStateText>
        </EmptyState>
      </DetailPanelCard>
    );
  }

  if (isError || (!isLoading && !detail)) {
    return (
      <DetailPanelCard data-testid="noa-detail-panel">
        <NoDocumentState data-testid="noa-detail-error">
          <NoDocumentIconTile>
            <Icon
              name={IconNames.Alerts_WarningOutline}
              id="noa-detail-error-icon"
              size="md"
              color={theme.colors.greyscale.gray40}
            />
          </NoDocumentIconTile>
          <NoDocumentTitle>{t('detail.errorTitle')}</NoDocumentTitle>
          <NoDocumentText>{t('detail.errorText')}</NoDocumentText>
        </NoDocumentState>
      </DetailPanelCard>
    );
  }

  if (isLoading || !detail) {
    return (
      <DetailPanelCard data-testid="noa-detail-panel">
        <StateWrapper data-testid="noa-detail-loading">
          <LoadingIndicator
            show
            size={SpinnerSizes.regular}
            message={t('detail.loading')}
          />
        </StateWrapper>
      </DetailPanelCard>
    );
  }

  const { carrier, documents } = detail;
  const isReleased = carrier.status === CarrierNoaStatus.Expired;
  const latestFirst = [...documents].sort(
    (a, b) => Number(b.isLatest) - Number(a.isLatest),
  );

  const detailCarrier = { ...carrier, hasNoaDocument: documents.length > 0 };
  const carrierBadge = resolveCarrierBadge(detailCarrier);

  const latestType = latestFirst[0]?.documentType;
  const isLorSection = latestType
    ? latestType === NoaDocumentType.Lor || latestType === NoaDocumentType.Slor
    : isReleased;

  return (
    <DetailPanelCard data-testid="noa-detail-panel">
      <DetailHeader>
        <DetailCarrierName>{carrier.carrierName}</DetailCarrierName>
        <StatusBadge badge={carrierBadge} testId="noa-detail-badge" />
      </DetailHeader>
      <DetailMeta>
        {carrier.mcNumber && <span>MC# {carrier.mcNumber}</span>}
        {carrier.dotNumber && <span>DOT# {carrier.dotNumber}</span>}
      </DetailMeta>

      <DetailSectionLabel>
        {isLorSection ? t('detail.lorDocument') : t('detail.noaDocument')}
      </DetailSectionLabel>
      {latestFirst.length > 0 ? (
        latestFirst.map((doc) => (
          <NoaDocumentCard
            key={doc.documentId}
            document={doc}
            carrierName={carrier.carrierName}
            statusBadge={carrierBadge}
            isReleased={isReleased}
          />
        ))
      ) : (
        <NoDocumentState data-testid="noa-no-document">
          <NoDocumentIconTile>
            <Icon
              name={IconNames.Filetype_Document}
              id="noa-no-document-icon"
              size="md"
              color={theme.colors.greyscale.gray40}
            />
          </NoDocumentIconTile>
          <NoDocumentTitle>{t('emptyStates.noDocumentTitle')}</NoDocumentTitle>
          <NoDocumentText>{t('emptyStates.noDocumentText')}</NoDocumentText>
        </NoDocumentState>
      )}
    </DetailPanelCard>
  );
};
